import React, { useMemo, useState } from 'react';
import { Brain, LogOut, Edit2, Check, X } from 'lucide-react';
import { SafeIcon } from '../../contexts/nobishiro-dashboard/SVGErrorBoundary';
import { useAuth } from '../../AuthContext';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { db, auth } from '../../firebase';

const NG_WORDS = ['死ね','殺す','バカ','馬鹿','アホ','クズ','ゴミ','fuck','shit','bitch','asshole'];
const containsNgWord = (t) => NG_WORDS.some(w => t.toLowerCase().includes(w.toLowerCase()));

const DashboardHeader = ({ 
  targetUserId, 
  setTargetUserId, 
  isAdmin, 
  signOut,
  allUserProfiles
}) => {
  const { user, profile } = useAuth();
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue]     = useState('');
  const [nameSaving, setNameSaving]   = useState(false);
  const [nameError, setNameError]     = useState('');
  const [nameSuccess, setNameSuccess] = useState(false);

  const currentName = profile?.nickname || profile?.username || profile?.displayName || user?.displayName || '';

  const handleNameSave = async () => {
    const v = nameValue.trim();
    if (!v) { setNameError('入力してください'); return; }
    if (v.length < 2) { setNameError('2文字以上'); return; }
    if (v.length > 12) { setNameError('12文字以内'); return; }
    if (containsNgWord(v)) { setNameError('使用できない言葉が含まれています'); return; }
    setNameSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        nickname: v, username: v, displayName: v,
        nicknameUpdatedAt: serverTimestamp(),
      });
      if (auth.currentUser) await updateProfile(auth.currentUser, { displayName: v });
      setNameSuccess(true);
      setEditingName(false);
      setTimeout(() => setNameSuccess(false), 3000);
      // ページリロードで反映
      window.location.reload();
    } catch(e) {
      setNameError('保存に失敗しました');
    } finally {
      setNameSaving(false);
    }
  };
  
  const userList = useMemo(() => {
    if (!allUserProfiles) return [];
    const list = Object.keys(allUserProfiles).filter(uid => allUserProfiles[uid]?.role !== 'master');
    return list.sort((a, b) => {
        if (a === user.uid) return -1;
        if (b === user.uid) return 1;
        return (allUserProfiles[a]?.username || '').localeCompare(allUserProfiles[b]?.username || '');
    });
  }, [allUserProfiles, user]);

  return (
    <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div className="flex items-center">
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-3 rounded-xl mr-4 shadow-lg"><SafeIcon IconComponent={Brain} className="h-7 w-7" /></div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight">NOBISHIROコンピテンシー®</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            {!editingName ? (
              <>
                <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 700 }}>
                  {currentName || '名前未設定'}
                </span>
                {nameSuccess && (
                  <span style={{ fontSize: 11, color: '#059669', fontWeight: 600, background: '#dcfce7', padding: '2px 8px', borderRadius: 20 }}>
                    ✅ 更新しました
                  </span>
                )}
                <button
                  onClick={() => { setNameValue(currentName); setEditingName(true); setNameError(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    border: 'none', borderRadius: 20,
                    padding: '4px 12px', fontSize: 11, fontWeight: 700,
                    color: '#fff', cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
                  }}
                >
                  <Edit2 size={11} /> ニックネーム変更
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={nameValue}
                      onChange={e => { setNameValue(e.target.value); setNameError(''); }}
                      maxLength={12}
                      autoFocus
                      placeholder="ニックネームを入力（2〜12文字）"
                      onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') setEditingName(false); }}
                      style={{
                        border: `2px solid ${nameError ? '#ef4444' : '#6366f1'}`,
                        borderRadius: 10, padding: '7px 12px',
                        fontSize: 14, outline: 'none', width: 200,
                        color: '#111827', background: '#fff',
                        boxShadow: '0 2px 8px rgba(99,102,241,0.15)',
                      }}
                    />
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: nameValue.length > 10 ? '#f59e0b' : '#9ca3af' }}>
                      {nameValue.length}/12
                    </span>
                  </div>
                  <button onClick={handleNameSave} disabled={nameSaving}
                    style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', border: 'none', borderRadius: 10, padding: '7px 16px', cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Check size={13} /> {nameSaving ? '保存中...' : '保存'}
                  </button>
                  <button onClick={() => setEditingName(false)}
                    style={{ background: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '7px 12px', cursor: 'pointer', color: '#6b7280', fontSize: 12, fontWeight: 600 }}>
                    キャンセル
                  </button>
                </div>
                {nameError && (
                  <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>⚠️ {nameError}</span>
                )}
                <span style={{ fontSize: 11, color: '#9ca3af' }}>Enterで保存・Escでキャンセル</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 self-end sm:self-center">
        {isAdmin && (
          <a href="/admin" style={{ fontSize: 11, padding: '6px 14px',
            background: '#1e1b4b', color: '#a5b4fc', borderRadius: 8,
            textDecoration: 'none', fontWeight: 700, border: '1px solid #312e81' }}>
            ⚙️ 管理者パネル
          </a>
        )}
        {isAdmin && userList.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <label htmlFor="user-select" className="text-xs font-medium text-gray-600 block mb-1">表示ユーザー</label>
            <select id="user-select" value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm bg-white">
              {userList.map(uid => (<option key={uid} value={uid}>{allUserProfiles[uid]?.username || uid}</option>))}
            </select>
          </div>
        )}
        <button onClick={signOut} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg shadow-sm hover:bg-gray-700 transition-colors text-sm font-medium"><SafeIcon IconComponent={LogOut} className="h-4 w-4" /><span className="hidden sm:inline">ログアウト</span></button>
      </div>
    </header>
  );
};
export default DashboardHeader;
